<template>
    <div class="calendar-wrapper week-display-root">
        <v-progress-linear v-if="loading" indeterminate color="white" class="mb-2 rounded-sm" />
        <div v-if="!loading && !hasAnyAvailability" class="empty-hint pa-4 text-center">
            <span class="empty-hint-text">{{ emptyMessage }}</span>
        </div>
        <template v-else-if="!loading">
            <div v-if="!isMobileCalendar" class="calendar-scroll">
                <div class="calendar-header">
                    <div class="time-col-header" aria-hidden="true" />
                    <div
                        v-for="(day, dayIndex) in days"
                        :key="day.key"
                        class="day-col"
                        :class="{ 'current-day': dayIndex === currentDayIndex }"
                    >
                        {{ day.label }}
                    </div>
                </div>

                <div class="calendar-grid">
                    <div class="time-col time-col-body">
                        <div v-for="(h, i) in hourLabels" :key="'h-' + i" class="time-label">
                            {{ h }}
                        </div>
                    </div>

                    <div class="days-col">
                        <div
                            v-for="day in days"
                            :key="day.key"
                            class="day-column"
                            :class="{ 'current-day': dayIndexForKey(day.key) === currentDayIndex }"
                        >
                            <div class="hour-cells" aria-hidden="true">
                                <div
                                    v-for="(h, i) in hours"
                                    :key="day.key + '-h-' + i"
                                    class="hour-cell"
                                />
                            </div>
                            <div class="day-events-layer">
                                <div
                                    v-for="seg in calendarSegmentsForDayKey(day.key)"
                                    :key="seg.id"
                                    class="event-block event-block--span"
                                    :style="segmentBlockStyle(seg)"
                                >
                                    <span class="event-time">{{ seg.label }}</span>
                                    <span class="event-title">Available</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="mobile-calendar">
                <v-card v-for="day in days" :key="day.key" class="mb-2 mobile-day-card" outlined>
                    <v-card-text class="py-2 px-3">
                        <div class="d-flex justify-space-between align-center">
                            <strong>{{ day.label }}</strong>
                            <span v-if="day.start && day.end" class="mobile-range">{{
                                formatRangeDisplay(day.start, day.end)
                            }}</span>
                            <span v-else class="grey--text text--darken-1">Not set</span>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </template>
    </div>
</template>

<script>
import { DAY_ORDER, to12hFromMinutes } from '@/utils/studentAvailabilityShared'

export default {
  name: 'StudentAvailabilityWeekDisplay',
  props: {
    days: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: 'No availability has been set for this week.'
    }
  },
  data () {
    return {
      hours: Array.from({ length: 24 }, (_, i) => i)
    }
  },
  computed: {
    hasAnyAvailability () {
      return (this.days || []).some((d) => d.start && d.end)
    },
    hourLabels () {
      return this.hours.map((h) => to12hFromMinutes(h * 60))
    },
    timeOptions () {
      return Array.from({ length: 48 }, (_, i) => to12hFromMinutes(i * 30))
    },
    isMobileCalendar () {
      return this.$vuetify.breakpoint.width < 650
    },
    currentDayIndex () {
      const js = new Date().getDay()
      const keyFromJs = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][js]
      return DAY_ORDER.indexOf(keyFromJs)
    }
  },
  methods: {
    dayIndexForKey (key) {
      return DAY_ORDER.indexOf(key)
    },
    toMinutesFrom12h (value) {
      if (value === null || value === undefined || value === '') return null
      const trimmed = String(value).trim()
      const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
      if (!match) return null
      let h = Number(match[1])
      const m = Number(match[2])
      const suf = match[3].toUpperCase()
      if (h < 1 || h > 12 || m < 0 || m > 59 || Number.isNaN(h) || Number.isNaN(m)) return null
      if (suf === 'PM' && h !== 12) h += 12
      if (suf === 'AM' && h === 12) h = 0
      return h * 60 + m
    },
    calendarSegmentsFromDay (day) {
      if (!day.start || !day.end) return []
      const s = this.toMinutesFrom12h(day.start)
      const e = this.toMinutesFrom12h(day.end)
      if (s === null || e === null || s === e) return []

      const lastSlotMinutes = (this.timeOptions.length - 1) * 30
      const isFullDaySlot =
                s === 0 &&
                e === lastSlotMinutes &&
                day.start === this.timeOptions[0] &&
                day.end === this.timeOptions[this.timeOptions.length - 1]

      const label = `${day.start} – ${day.end}`
      const idx = DAY_ORDER.indexOf(day.key)

      if (isFullDaySlot) {
        return [{ dayKey: day.key, startMin: 0, endMin: 1440, label: `${label} (all day)` }]
      }
      if (e > s) {
        return [{ dayKey: day.key, startMin: s, endMin: e, label }]
      }
      if (e < s) {
        const nextKey = DAY_ORDER[(idx + 1) % 7]
        return [
          { dayKey: day.key, startMin: s, endMin: 1440, label },
          { dayKey: nextKey, startMin: 0, endMin: e, label }
        ]
      }
      return []
    },
    allCalendarSegments () {
      const out = []
      for (const day of this.days) {
        out.push(...this.calendarSegmentsFromDay(day))
      }
      return out
    },
    calendarSegmentsForDayKey (dayKey) {
      let i = 0
      return this.allCalendarSegments()
        .filter((seg) => seg.dayKey === dayKey)
        .map((seg) => ({
          ...seg,
          id: `${seg.dayKey}-${seg.startMin}-${seg.endMin}-${i++}`
        }))
    },
    segmentBlockStyle (seg) {
      const total = 1440
      const top = (seg.startMin / total) * 100
      const height = ((seg.endMin - seg.startMin) / total) * 100
      return {
        top: `${top}%`,
        height: `${height}%`
      }
    },
    formatRangeDisplay (start, end) {
      if (!start || !end) return ''
      let s = this.toMinutesFrom12h(start)
      let e = this.toMinutesFrom12h(end)
      if (s === null) s = this.toMinutesFrom12h(this.normalizeLegacyTime(start))
      if (e === null) e = this.toMinutesFrom12h(this.normalizeLegacyTime(end))
      if (s !== null && e !== null) {
        return `${to12hFromMinutes(s)} – ${to12hFromMinutes(e)}`
      }
      return `${start} – ${end}`
    },
    normalizeLegacyTime (t) {
      const [hh, mm] = String(t).split(':').map(Number)
      if (Number.isNaN(hh) || Number.isNaN(mm)) return t
      return to12hFromMinutes(hh * 60 + mm)
    }
  }
}
</script>

<style scoped>
.week-display-root {
    overflow: hidden;
}

@media (max-width: 959px) {
    .week-display-root {
        overflow: visible;
    }
}

.calendar-scroll {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 12px;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: min(70vh, 720px);
}

.empty-hint {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
}

.empty-hint-text {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
}

.calendar-header {
    display: grid;
    grid-template-columns: 64px repeat(7, minmax(0, 1fr));
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    font-weight: 600;
    color: #fdfdfd;
    align-items: center;
}

.time-col-header {
    min-height: 1px;
}

.calendar-grid {
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
}

.time-col-body {
    display: grid;
    grid-template-rows: repeat(24, 48px);
    border-right: 1px solid rgba(255, 255, 255, 0.18);
    align-items: stretch;
}

.time-label {
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    padding: 4px 4px 0 0;
    font-size: 0.72rem;
    line-height: 1.2;
    text-align: right;
    color: rgba(255, 255, 255, 0.8);
}

.days-col {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    align-items: stretch;
    min-width: 0;
}

.day-column {
    position: relative;
    min-width: 0;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.day-column:last-child {
    border-right: none;
}

.hour-cells {
    display: grid;
    grid-template-rows: repeat(24, 48px);
    background: rgba(255, 255, 255, 0.02);
}

.hour-cell {
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.day-column.current-day .hour-cells {
    background: rgba(255, 255, 255, 0.06);
}

.day-events-layer {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
}

.day-col {
    text-align: center;
    padding: 8px 4px;
    font-size: 0.85rem;
}

.day-col.current-day {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 6px 6px 0 0;
}

.event-block {
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    padding: 4px 6px;
    background: #f5fff7;
    font-size: 0.68rem;
    line-height: 1.2;
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    z-index: 1;
}

.event-block--span {
    position: absolute;
    left: 4px;
    right: 4px;
    min-height: 18px;
    pointer-events: auto;
    box-sizing: border-box;
}

.event-time {
    color: #455a64;
}

.event-title {
    font-weight: 500;
    margin-top: 2px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.mobile-day-card {
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.95);
}

.mobile-range {
    font-size: 0.85rem;
    text-align: right;
    max-width: 62%;
}

.mobile-calendar {
    display: block;
}

@media (max-width: 959px) {
    .calendar-scroll {
        padding: 8px;
        max-height: none;
    }

    .calendar-header {
        grid-template-columns: 56px repeat(7, minmax(0, 1fr));
    }

    .calendar-grid {
        grid-template-columns: 56px minmax(0, 1fr);
    }

    .time-col-body {
        grid-template-rows: repeat(24, 40px);
    }

    .hour-cells {
        grid-template-rows: repeat(24, 40px);
    }

    .event-block {
        font-size: 0.62rem;
        padding: 3px 4px;
    }
}

@media (max-width: 649px) {
    .calendar-scroll {
        display: none;
    }
}
</style>
